// Peças da Visão Geral por perfil (#6493). Toda tela de perfil monta a mesma ordem:
// Cabeçalho → Hoje → Meu mês → Detalhe → Atalhos (decisão aprovada em 27/10/2026).
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDownRight, ArrowUpRight, Calendar, Minus } from 'lucide-react';
import type { PeriodoVisaoGeral } from '../../src/lib/visaoGeral/periodo';
import { mesDe, saudacao } from '../../src/lib/visaoGeral/periodo';

export type Tom = 'critico' | 'atencao' | 'ok' | 'neutro';

const TOM_HOJE: Record<Tom, string> = {
  critico: 'bg-red-50 border-red-100 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-300',
  atencao: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-900/20 dark:border-amber-900/40 dark:text-amber-300',
  ok: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-900/40 dark:text-emerald-300',
  neutro: 'bg-white border-gray-100 text-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100',
};

/** Link interno, botão ou bloco parado — conforme o cartão leva a algum lugar ou não. */
const Clicavel: React.FC<{ para?: string; onClick?: () => void; className: string; title?: string; children: React.ReactNode }> = ({ para, onClick, className, title, children }) => {
  if (para) return <Link to={para} className={`${className} hover:shadow-md transition-shadow`} title={title}>{children}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={`${className} text-left hover:shadow-md transition-shadow`} title={title}>{children}</button>;
  return <div className={className} title={title}>{children}</div>;
};

// ── Cabeçalho ──

interface CabecalhoProps {
  nome: string | null | undefined;
  pergunta: string;
  periodo: PeriodoVisaoGeral;
  aoMudarMes?: (mes: string) => void;
  /** Botões de ação rápida do perfil. */
  acoes?: React.ReactNode;
}

export const CabecalhoVisaoGeral: React.FC<CabecalhoProps> = ({ nome, pergunta, periodo, aoMudarMes, acoes }) => {
  const hoje = new Date();
  const primeiroNome = (nome || '').trim().split(/\s+/)[0] || 'tudo bem';
  return (
    <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl md:text-3xl font-poppins font-bold text-gray-900 dark:text-white">
          {saudacao(hoje)}, {primeiroNome}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{pergunta}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {acoes}
        {periodo.tipo === 'mes' && aoMudarMes ? (
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 shadow-sm">
            <label htmlFor="vg-mes" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Mês</label>
            <input
              id="vg-mes"
              type="month"
              value={periodo.mes}
              max={mesDe(hoje)}
              onChange={(e) => e.target.value && aoMudarMes(e.target.value)}
              className="text-sm bg-transparent text-gray-900 dark:text-white outline-none dark:[color-scheme:dark]"
            />
            {!periodo.corrente && (
              <button type="button" onClick={() => aoMudarMes(mesDe(hoje))} className="text-xs font-semibold text-primary hover:underline">
                Mês atual
              </button>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5">
            <Calendar size={14} /> {periodo.rotulo}
          </span>
        )}
      </div>
    </header>
  );
};

// ── Seção ──

export const SecaoVisaoGeral: React.FC<{ titulo: string; subtitulo?: string; children: React.ReactNode; colunas?: 2 | 3 | 4 | 5 }> = ({ titulo, subtitulo, children, colunas = 4 }) => {
  // No celular, dois cartões por linha: um por linha empurra o resto da tela para longe.
  const grade = colunas === 2 ? 'grid-cols-2' : colunas === 3 ? 'grid-cols-2 lg:grid-cols-3'
    : colunas === 5 ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4';
  return (
    <section className="space-y-3" aria-label={titulo}>
      <div className="flex items-baseline gap-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{titulo}</h2>
        {subtitulo && <span className="text-xs text-gray-400 dark:text-gray-500">{subtitulo}</span>}
      </div>
      <div className={`grid ${grade} gap-3 md:gap-4`}>{children}</div>
    </section>
  );
};

// ── Cartão do "Hoje": quanto tem para fazer agora ──

interface CartaoHojeProps {
  /** null = carregando. */
  valor: number | string | null;
  rotulo: string;
  /** Tom quando há algo a fazer; zero sempre fica neutro. */
  tom?: Tom;
  para?: string;
  onClick?: () => void;
  dica?: string;
  /** Linha curta abaixo do rótulo (ex.: a quantidade, quando o valor é kWh). */
  detalhe?: string;
  /** Força o tom neutro quando o valor não é numérico (ex.: "0 kWh"). */
  vazio?: boolean;
}

export const CartaoHoje: React.FC<CartaoHojeProps> = ({ valor, rotulo, tom = 'neutro', para, onClick, dica, detalhe, vazio }) => {
  const efetivo: Tom = valor === 0 || vazio ? 'neutro' : tom;
  return (
    <Clicavel para={para} onClick={onClick} title={dica} className={`block rounded-2xl border p-4 shadow-sm ${TOM_HOJE[efetivo]}`}>
      {valor === null ? (
        <span className="block h-8 w-12 rounded-md bg-current opacity-10 animate-pulse" aria-label="Carregando" />
      ) : (
        <span className="block text-3xl font-montserrat font-bold tabular-nums">{valor}</span>
      )}
      <span className={`block text-sm mt-1 ${efetivo === 'neutro' ? 'text-gray-500 dark:text-gray-400' : ''}`}>{rotulo}</span>
      {detalhe && valor !== null && <span className="block text-xs mt-0.5 opacity-75 tabular-nums">{detalhe}</span>}
    </Clicavel>
  );
};

// ── Cartão de indicador do mês: valor, meta e comparação ──

interface CartaoIndicadorProps {
  titulo: string;
  /** null = carregando. */
  valor: string | null;
  detalhe?: string;
  /** Meta: texto ("meta 92 mil kWh") e quanto já foi atingido, de 0 a 1. */
  meta?: { texto: string; atingido: number | null };
  /** Comparação justa com o período anterior. `bom`: verde, vermelho ou neutro. */
  comparacao?: { texto: string; bom: boolean | null; sobe: boolean | null };
  para?: string;
  /** De onde vem o número (aparece ao passar o mouse). */
  origem?: string;
  /** De onde veio cada parte do total (ex.: kWh por canal). Destaque = parte que pede atenção. */
  composicao?: { rotulo: string; valor: string; destaque?: boolean; dica?: string }[];
  /** Observação curta abaixo de tudo (ex.: o que distorce a comparação). */
  nota?: string;
}

export const CartaoIndicador: React.FC<CartaoIndicadorProps> = ({ titulo, valor, detalhe, meta, comparacao, para, origem, composicao, nota }) => {
  const pct = meta?.atingido != null ? Math.max(0, Math.min(1, meta.atingido)) : null;
  const corBarra = pct == null ? 'bg-primary' : pct >= 1 ? 'bg-emerald-500' : pct >= 0.7 ? 'bg-amber-500' : 'bg-red-500';
  const Seta = comparacao?.sobe == null ? Minus : comparacao.sobe ? ArrowUpRight : ArrowDownRight;
  const corComparacao = comparacao?.bom == null
    ? 'text-gray-500 bg-gray-100 dark:text-gray-300 dark:bg-gray-700'
    : comparacao.bom
      ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30'
      : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30';
  return (
    <Clicavel para={para} title={origem} className="block min-w-0 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm">
      <span className="block text-sm text-gray-500 dark:text-gray-400">{titulo}</span>
      {valor === null ? (
        <span className="block h-8 w-24 mt-1 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse" aria-label="Carregando" />
      ) : (
        <span className="block text-2xl md:text-3xl font-montserrat font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums break-words">{valor}</span>
      )}
      {meta && (
        <span className="block mt-2">
          <span className="block h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <span className={`block h-full rounded-full ${corBarra}`} style={{ width: `${(pct ?? 0) * 100}%` }} />
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{meta.texto}</span>
        </span>
      )}
      {detalhe && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{detalhe}</span>}
      {composicao && composicao.length > 0 && (
        <span className="block mt-2 space-y-0.5">
          {composicao.map(c => (
            <span key={c.rotulo} title={c.dica} className={`flex items-baseline justify-between gap-2 text-xs ${c.destaque ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}`}>
              <span className="min-w-0 truncate">{c.rotulo}</span>
              <span className="tabular-nums whitespace-nowrap">{c.valor}</span>
            </span>
          ))}
        </span>
      )}
      {comparacao && (
        <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold rounded-full px-2 py-0.5 ${corComparacao}`}>
          <Seta size={12} /> {comparacao.texto}
        </span>
      )}
      {nota && <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-2">{nota}</span>}
    </Clicavel>
  );
};

// ── Painel de detalhe (lista, tabela, gráfico) ──

export const PainelDetalhe: React.FC<{ titulo: string; acao?: { rotulo: string; para: string }; children: React.ReactNode; className?: string }> = ({ titulo, acao, children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm min-w-0 ${className}`}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <h3 className="font-poppins font-bold text-gray-900 dark:text-white">{titulo}</h3>
      {acao && (
        <Link to={acao.para} className="text-sm font-semibold text-primary hover:underline whitespace-nowrap">{acao.rotulo}</Link>
      )}
    </div>
    {children}
  </div>
);

// ── Atalhos e ações rápidas ──

export interface Atalho { rotulo: string; para: string; icone?: React.ElementType }

export const ListaAtalhos: React.FC<{ itens: Atalho[] }> = ({ itens }) => {
  if (itens.length === 0) return null;
  return (
    <section aria-label="Atalhos" className="space-y-3">
      <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Atalhos</h2>
      <div className="flex flex-wrap gap-2">
        {itens.map(({ rotulo, para, icone: Icone }) => (
          <Link key={para + rotulo} to={para} className="inline-flex items-center gap-2 text-sm font-semibold text-primary dark:text-primary-light bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 rounded-xl px-3 py-2 transition-colors">
            {Icone && <Icone size={15} />} {rotulo} <ArrowRight size={14} />
          </Link>
        ))}
      </div>
    </section>
  );
};

export const BotaoAcao: React.FC<{ rotulo: string; icone: React.ElementType; onClick?: () => void; para?: string }> = ({ rotulo, icone: Icone, onClick, para }) => {
  const classe = 'inline-flex items-center gap-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl px-3.5 py-2 shadow-sm transition-colors';
  if (para) return <Link to={para} className={classe}><Icone size={15} /> {rotulo}</Link>;
  return <button type="button" onClick={onClick} className={classe}><Icone size={15} /> {rotulo}</button>;
};

/** Texto de "x tarefas" no singular ou plural. */
export const plural = (n: number, um: string, varios: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;
